import {useEffect, useState} from "react";
import {CustomerBankAccount} from "../../../../../api/customerBankAccounts/CustomerBankAccount.ts";
import {CustomerBankAccountRepository} from "../../../../../api/customerBankAccounts/CustomerBankAccountRepository.ts";
import {BackendApi} from "../../../../../api/api.ts";
import BankAccountSelector from "../../../../../components/specific/BankAccountSelector.tsx";
import Shell from "../../../../../components/specific/Shell.tsx";
import MonthlyInflowOutflowGraph from "./MonthlyInflowOutflowGraph.tsx";
import {User} from "../../../../../api/accessControl/User.ts";
import NarrowDrawer from "../../../../../components/general/NarrowDrawer.tsx";
import {DateTime} from "luxon";
import LoadingFrame from "../../../../../components/LoadingFrame.tsx";
import {MonthOverview, MonthOverviewDataSpec} from "./MonthOverview.tsx";
import {useSearchParams} from "react-router-dom";
import {CashflowPageState, useCashflowPageState} from "./CashflowPageState.ts";


function getDateStringFromTab(tabValue: number) {
    return new Date(tabValue, 0, 1).toISOString().split('T')[0]
}


export default function CashflowPage({currentUser}: { currentUser: User }) {
    const [queryParams, setQueryParams] = useSearchParams()
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isMonthOverviewOpen, setIsMonthOverviewOpen] = useState<boolean>(!!queryParams.get('tf'));

    const [selectedBar, setSelectedBar] = useCashflowPageState(queryParams, setQueryParams)
    const [monthOverviewData, setMonthOverviewData] = useState<MonthOverviewDataSpec>();
    const [monthSummary, setMonthSummary] = useState<string>();

    const [boiGraph, setBoiGraph] = useState();
    const [selectedTabs, setSelectedTabs] = useState([new Date().getFullYear()]);
    const [bankAccounts, setBankAccounts] = useState<Array<CustomerBankAccount>>([]);
    const [selectedBankAccountId, setSelectedBankAccountId] = useState<string>();

    const api = new BackendApi(currentUser.getToken())


    useEffect(() => {
        async function onLoad() {
            const bankAccountsResponse = await new CustomerBankAccountRepository(api)
                .getMyBankAccounts()

            setBankAccounts(bankAccountsResponse)
            fetchBoiGraphData(selectedBankAccountId)

            if (isMonthOverviewOpen) {
                fetchMonthOverviewData(new Date(selectedBar?.month))
            }

        }

        onLoad()
    }, []);


    async function fetchBoiGraphData(bankAccountId: string | null = null) {
        const response = await api.Business__stats__iobGraphData(
            bankAccountId,
            getDateStringFromTab(Math.min(...selectedTabs)),
            getDateStringFromTab(Math.max(...selectedTabs) + 1)
        )

        setBoiGraph((_) => {
                setIsLoading(false)
                return response
            }
        )
    }

    function handleDropdownChange(bankAccount: CustomerBankAccount | null) {
        setSelectedBankAccountId(bankAccount?.id)
        fetchBoiGraphData(bankAccount?.id)
    }

    const thisYear = new Date().getFullYear()
    const years = [thisYear - 2, thisYear - 1, thisYear];

    useEffect(() => {
        fetchBoiGraphData(selectedBankAccountId)
    }, [selectedTabs]);

    async function fetchMonthOverviewData(month: Date): Promise<void> {
        const results = await Promise.all([
            api.Business__stats__monthOverview(month.getFullYear(), month.getMonth() + 1, selectedBankAccountId || null), api.Business__getMonthlySummary(month.getFullYear(), month.getMonth() + 1, selectedBankAccountId || null)])

        setMonthOverviewData(results[0])
        setMonthSummary(results[1].summary)
    }

    async function handleBarClick(data: CashflowPageState) {
        setSelectedBar(data)
        setIsMonthOverviewOpen(true)
        fetchMonthOverviewData(new Date(data.month))
    }

    return (
        <>
            <NarrowDrawer open={!!isMonthOverviewOpen} title={'Maand overzicht'}
                          updateOpenState={() => {
                              setIsMonthOverviewOpen(false)
                              setSelectedBar(null)
                          }}>
                <LoadingFrame isLoading={!(selectedBar)}>
                    <MonthOverview data={monthOverviewData}
                                   monthlySummary={monthSummary}
                                   currentMonth={new Date(selectedBar?.month)}
                                   flow={selectedBar?.flow}
                                   isLoading={!monthOverviewData}
                                   onDateChange={(change) => {

                                       let newDate = DateTime.fromISO(selectedBar?.month).minus({months: 1})
                                       if (change > 0)
                                           newDate = DateTime.fromISO(selectedBar?.month).plus({months: 1})

                                       setSelectedBar({
                                           ...selectedBar,
                                           month: newDate.toISO(),
                                       })
                                       setMonthOverviewData(null)
                                       fetchMonthOverviewData(newDate.toJSDate())
                                   }}
                                   onFlowChange={(newFlow) => {
                                       setSelectedBar({
                                           ...selectedBar,
                                           flow: newFlow
                                       })
                                   }}
                    />

                </LoadingFrame>

            </NarrowDrawer>
            <Shell currentUser={currentUser} isLoading={isLoading} title={
                <div className="w-full flex items-center justify-between">
                    <div className="flex items-center gap-x-6">
                        <h1 className="weight-600 text-3xl">Cashflow</h1>


                    </div>
                    <BankAccountSelector label="Bankrekening" allOptionLabel="Alle"
                                         bankAccounts={bankAccounts}
                                         selectedAccountId={selectedBankAccountId}
                                         onSelect={handleDropdownChange}/>
                    <div className="">
                        <nav aria-label="Tabs" className="flex space-x-4">
                            {years.map((tab) => (
                                <button
                                    key={tab}
                                    onClick={
                                        () => {
                                            if (selectedTabs.includes(tab)) {
                                                if (selectedTabs.length === 1)
                                                    return
                                                setSelectedTabs((prev) => prev.filter((year) => year !== tab))
                                            } else {
                                                setSelectedTabs((prev) => [...prev, tab])
                                            }
                                        }
                                    }
                                    className={(selectedTabs.includes(tab) ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-700') + 'rounded-md px-3 py-2 text-sm font-medium rounded-md'}
                                >
                                    {tab}
                                </button>
                            ))}
                        </nav>
                    </div>

                </div>
            }>
                <MonthlyInflowOutflowGraph data={boiGraph} className={"h-[90vh]"}
                                           onBarClick={handleBarClick}
                                           selectedBar={selectedBar}></MonthlyInflowOutflowGraph>

            </Shell>
        </>
    )
}
